import { arcgisToGeoJSON, geojsonToArcGIS } from '@terraformer/arcgis';
import { MapDrawFuncParams, GeocodeType, AddressType } from 'types';
import { v4 as UUIDV4 } from 'uuid';
import OpenLocationCode from 'open-location-code-typescript';
import MapSettings from 'components/Map/settings';
import Util from 'utilities/helper.util';
import { MapGraphicError } from './error';
import { EnquiryShape } from '../../localStore/types';

const scale = window.devicePixelRatio || 1;
const nameOffset = scale > 2 ? '@3x' : scale > 1 ? '@2x' : '';

export const geocodeToAddress = (geocode: GeocodeType): AddressType => {
  const { formattedaddress, locality, region, postcode } = geocode;
  const breakpoint = formattedaddress.indexOf(`, ${locality}, ${region}`);
  const newAddress = formattedaddress.substring(0, breakpoint);
  const addressWithLevel = /^((suite|shop|unit)\s.+,\s)?Level\s.+,/i.exec(
    newAddress
  );
  const trimedAddress = Array.isArray(addressWithLevel)
    ? newAddress.replace(addressWithLevel[0], '')
    : newAddress;

  return {
    line1: Util.toTitleCase(trimedAddress),
    line2: '',
    country: 'AUS',
    locality: Util.toTitleCase(locality),
    postcode,
    state: region
  };
};

/**
 * Encode a location into an Open Location Code.
 *
 * @param {number} latitude The latitude in signed decimal degrees. It will
 *     be clipped to the range -90 to 90.
 * @param {number} longitude The longitude in signed decimal degrees. Will be
 *     normalised to the range -180 to 180.
 * @return {string} The code.
 * @throws {Exception} if any of the input values are not numbers.
 */
export const encodeOpenLocationCode = (latitude: number, longitude: number) => {
  return OpenLocationCode.encode(latitude, longitude);
};

export const getLiveArea = ({ graphic, graphics }: MapDrawFuncParams) => {
  const { loadedMapModules } = window;
  const { GeometryEngine } = loadedMapModules;
  const areaUnit = 'square-kilometers';
  const lengthUnit = 'kilometers';
  const drawedGraphic = graphics !== undefined ? graphics[0] : graphic;

  if (drawedGraphic.geometry.type === 'polyline') {
    return GeometryEngine.geodesicLength(drawedGraphic.geometry, lengthUnit);
  } else {
    return GeometryEngine.geodesicArea(drawedGraphic.geometry, areaUnit);
  }
};

export const deleteGraphics = ({
  graphics,
  drawingLayer
}: MapDrawFuncParams) => {
  if (Array.isArray(graphics) && drawingLayer) {
    drawingLayer.removeMany(graphics);
  }
};

export const addGraphics = ({
  graphics,
  drawingLayer,
  tool,
  userReference
}: MapDrawFuncParams) => {
  if (Array.isArray(graphics)) {
    const shapes = graphics.map((graphic, idx) => {
      const { polygonInGeoJson, centroid, geometryArea, uid } = addGraphic({
        graphic,
        drawingLayer,
        tool,
        graphics: undefined
      });
      return {
        geoJson: polygonInGeoJson,
        area: geometryArea,
        centroid,
        shapeId: UUIDV4(),
        uid,
        userReference: userReference[idx]
      };
    });

    return shapes;
  }

  return [];
};

export const addGraphic = ({
  graphic,
  graphics,
  drawingLayer,
  tool
}: MapDrawFuncParams) => {
  const { loadedMapModules } = window;
  const { GeometryEngine, WebMercatorUtils, Graphic } = loadedMapModules;
  const maxAreaUnit = 'square-kilometers';
  const maxArea = 20;

  let drawedGraphic = graphics !== undefined ? graphics[0] : graphic;

  drawingLayer.remove(drawedGraphic);
  if (
    drawedGraphic === undefined ||
    (drawedGraphic?.geometry?.type !== 'polygon' &&
      drawedGraphic?.geometry?.type !== 'polyline')
  ) {
    throw new MapGraphicError('Invalid type geometry.', drawedGraphic.uid);
  }

  if (drawedGraphic.geometry.type === 'polyline') {
    const buffer = GeometryEngine.geodesicBuffer(
      drawedGraphic.geometry,
      10,
      'meters'
    );
    drawedGraphic = new Graphic({
      geometry: buffer,
      symbol: MapSettings.polygonSymbol
    });
  }

  // Throw error if circle diameter less than 10m diameter
  if (drawedGraphic.geometry.extent.width < 10 && tool === 'circle') {
    throw new MapGraphicError(
      'Circle diameter must be more than 10m.',
      drawedGraphic.uid
    );
  }

  // Buffer small polygon to minimum of 10m for both extent width and height.
  // We already block circle, no need buffer
  // Polyline will always buffer to 10m upon drawing, no need
  if (tool === 'polygon') {
    if (
      drawedGraphic.geometry.extent.width < 10 ||
      drawedGraphic.geometry.extent.height < 10
    ) {
      const lowestMeasurement =
        drawedGraphic.geometry.extent.width <
        drawedGraphic.geometry.extent.height
          ? drawedGraphic.geometry.extent.width
          : drawedGraphic.geometry.extent.height;

      // Division of 2 to make the minimum width/height near to 10m
      // Trying not to bump too much which might end up distort the original intention of the dig site
      const buffer = GeometryEngine.geodesicBuffer(
        drawedGraphic.geometry,
        (10 - Math.floor(lowestMeasurement)) / 2,
        'meters'
      );
      if (!buffer) {
        //Getting null after buffer if both line are too close together even not Self-intersecting
        throw new MapGraphicError(
          'Self-intersecting polygon has been detected and is not supported.',
          drawedGraphic.uid
        );
      }
      drawedGraphic = new Graphic({
        geometry: buffer,
        symbol: MapSettings.polygonSymbol
      });
    }
  }

  const geometry = drawedGraphic.geometry;
  const geoGraphicGeometry = WebMercatorUtils.webMercatorToGeographic(geometry);
  const polygonInGeoJson: any = arcgisToGeoJSON(geoGraphicGeometry);
  const geometryArea = GeometryEngine.geodesicArea(geometry, maxAreaUnit);

  if (
    polygonInGeoJson.type === 'MultiPolygon' ||
    geometry.isSelfIntersecting ||
    geometry.rings.length > 1
  ) {
    throw new MapGraphicError(
      'Self-intersecting polygon has been detected and is not supported.',
      drawedGraphic.uid
    );
  }

  if (geometryArea > maxArea) {
    const myError = new MapGraphicError(
      `The maximum is ${maxArea} square kilometres but your area is ${geometryArea.toFixed(
        1
      )} km²`,
      drawedGraphic.uid
    );
    myError.name = 'The shape is too large';
    throw myError;
  }

  const coords = polygonInGeoJson.coordinates[0];
  const xmin = coords.reduce(
    (agg: number, v: number[]) => (v[0] < agg ? v[0] : agg),
    180
  );
  const xmax = coords.reduce(
    (agg: number, v: number[]) => (v[0] > agg ? v[0] : agg),
    -180
  );
  const ymin = coords.reduce(
    (agg: number, v: number[]) => (v[1] < agg ? v[1] : agg),
    90
  );
  const ymax = coords.reduce(
    (agg: number, v: number[]) => (v[1] > agg ? v[1] : agg),
    -90
  );

  const AUSTRALIA_BBOX = {
    xmin: +95,
    xmax: +170,
    ymin: -45,
    ymax: -7
  };

  if (
    xmin > AUSTRALIA_BBOX.xmax ||
    xmax < AUSTRALIA_BBOX.xmin ||
    ymin > AUSTRALIA_BBOX.ymax ||
    ymax < AUSTRALIA_BBOX.ymin
  ) {
    const err = new MapGraphicError(
      'The job site must be within Australia',
      drawedGraphic.uid
    );
    err.name = 'Illegal job site';
    throw err;
  }

  const centroid = {
    latitude: geometry.centroid.latitude,
    longitude: geometry.centroid.longitude
  };

  drawingLayer.add(drawedGraphic);
  drawingLayer.emit('change');

  return { polygonInGeoJson, centroid, geometryArea, uid: drawedGraphic.uid };
};

export const clearGraphic = () => {
  const { loadedMap, loadedSketchViewModel } = window;
  if (loadedMap && loadedSketchViewModel) {
    loadedSketchViewModel.delete();
    loadedSketchViewModel.cancel();
    const drawingLayer = loadedMap.findLayerById('drawing-layer');
    const errorShapeLayer = loadedMap.findLayerById('error-shape-layer');
    const errorMarkerLayer = loadedMap.findLayerById('error-marker-layer');
    if (drawingLayer) {
      drawingLayer.removeAll();
    }
    if (errorShapeLayer) {
      errorShapeLayer.removeAll();
    }
    if (errorMarkerLayer) {
      errorMarkerLayer.removeAll();
    }
  }
};

export const showRecent = (flag: boolean) => {
  const { loadedMap } = window;
  if (loadedMap) {
    const layer = loadedMap.findLayerById('recent-enquiries-layer');
    if (layer) {
      layer.visible = flag;
    }
  }
};

export const getRecent = () => {
  const { loadedMap } = window;
  if (loadedMap) {
    const layer = loadedMap.findLayerById('recent-enquiries-layer');
    if (layer) {
      return layer.visible;
    }
  }

  return false;
};

export const getInstruction = (tool: string) => {
  switch (tool) {
    case 'circle':
      return 'Click and drag to draw then release to finish';
    case 'polyline':
      return 'Double-click to finish line';
    case 'polygon':
      return 'Double-click to close shape';
    default:
      return 'Draw your job site with the tools';
  }
};

export const refreshShowRecent = () => {
  const { loadedMap } = window;
  if (loadedMap) {
    const layer = loadedMap.findLayerById('recent-enquiries-layer');
    if (layer) {
      layer.refresh();
    }
  }
};

export const addShapesReference = async (shapes: EnquiryShape[]) => {
  const { loadedMapModules, loadedMap } = window;
  const { Graphic, WebMercatorUtils } = loadedMapModules;
  if (loadedMap && loadedMapModules) {
    const layer = loadedMap.findLayerById('shape-refer-layer');
    if (layer) {
      const edits: { deleteFeatures?: any[]; addFeatures?: any[] } = {};
      const currentData = await layer.queryFeatures();
      if (currentData.features?.length > 0) {
        edits.deleteFeatures = currentData.features;
      }
      const graphics = shapes.map((shape) => {
        const geometry = geojsonToArcGIS(shape.geoJson);
        const webMercatorGeometry =
          WebMercatorUtils.geographicToWebMercator(geometry);
        return new Graphic({
          attributes: {
            NAME: Util.middleTruncate(shape.userReference || '', 20)
          },
          geometry: {
            ...webMercatorGeometry,
            type: 'polygon'
          }
        });
      });
      edits.addFeatures = graphics;
      const results = await layer.applyEdits(edits);
      const objectIds: any[] = [];
      results?.addFeatureResults.forEach((item: any) => {
        objectIds.push(item.objectId);
      });
      layer.queryFeatures({ objectIds });
    }
  }
};

export const addErrorShapes = (
  geos: GeoJSON.GeometryObject[],
  marker = 'map-alert'
) => {
  const { loadedMapModules, loadedMap } = window;
  const { Graphic, WebMercatorUtils } = loadedMapModules;
  if (loadedMap && loadedMapModules) {
    const shapeLayer = loadedMap.findLayerById('error-shape-layer');
    const markerLayer = loadedMap.findLayerById('error-marker-layer');
    if (shapeLayer && markerLayer) {
      const shapeGraphics: __esri.Graphic[] = [];
      const markerGraphics: __esri.Graphic[] = [];
      geos.forEach((shape) => {
        const geometry = geojsonToArcGIS(shape);
        const webMercatorGeometry =
          WebMercatorUtils.geographicToWebMercator(geometry);
        const shapeGraphic = new Graphic({
          geometry: {
            ...webMercatorGeometry,
            type: 'polygon'
          },
          symbol: {
            type: 'simple-fill',
            style: 'forward-diagonal',
            color: [0, 0, 0, 0.5],
            outline: {
              style: 'none'
            }
          }
        });
        const markerGraphic = new Graphic({
          geometry: shapeGraphic.geometry.centroid,
          symbol: {
            type: 'picture-marker',
            url: `/${marker}${nameOffset}.png`,
            width: '16px',
            height: '16px'
          }
        });
        shapeGraphics.push(shapeGraphic);
        markerGraphics.push(markerGraphic);
      });
      shapeLayer.removeAll();
      shapeLayer.addMany(shapeGraphics);
      markerLayer.removeAll();
      markerLayer.addMany(markerGraphics);
    }
  }
};

export const clearShapesReference = async () => {
  const { loadedMap, loadedSketchViewModel } = window;
  if (loadedMap && loadedSketchViewModel) {
    const layer = loadedMap.findLayerById('shape-refer-layer');
    if (layer) {
      const currentData = await layer.queryFeatures();
      if (currentData.features?.length > 0) {
        const edits = {
          deleteFeatures: currentData.features
        };
        await layer.applyEdits(edits);
      }
    }
  }
};

export const clearErrorShapes = () => {
  const { loadedMap, loadedSketchViewModel } = window;
  if (loadedMap && loadedSketchViewModel) {
    const errorShapeLayer = loadedMap.findLayerById('error-shape-layer');
    const errorMarkerLayer = loadedMap.findLayerById('error-marker-layer');
    if (errorShapeLayer) {
      errorShapeLayer.removeAll();
    }
    if (errorMarkerLayer) {
      errorMarkerLayer.removeAll();
    }
  }
};

export const highlightMapShape = (mapShapeId?: number) => {
  const { loadedMap, loadedSketchViewModel } = window;
  if (loadedMap && loadedSketchViewModel) {
    const drawingLayer = loadedMap.findLayerById('drawing-layer');
    if (drawingLayer) {
      drawingLayer.graphics.forEach((g: any) => {
        g.symbol =
          mapShapeId === g.uid
            ? {
                type: 'simple-fill',
                color: 'rgba(0, 112, 255, 0.25)',
                style: 'solid',
                outline: {
                  color: 'rgba(250, 77, 86, 1)',
                  width: 2
                }
              }
            : MapSettings.polygonSymbol;
      });
    }
  }
};
